import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Package, Plus, Minus, History, Loader2, AlertTriangle, Building2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function OpsInventory({ user }) {
  const [inventory, setInventory] = useState([]);
  const [kitchenName, setKitchenName] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isAdjustDialogOpen, setIsAdjustDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [adjustment, setAdjustment] = useState({ value: 0, reason: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [report, setReport] = useState({ transactions: [], summary: {} });
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [reportFilter, setReportFilter] = useState("all");

  useEffect(() => {
    if (user) {
      fetchInventory();
    }
  }, [user]);

  const fetchInventory = async () => {
    try {
      const res = await axios.get(`${API}/api/ops-extended/my-kitchen-inventory?user_id=${user.user_id}`);
      setInventory(res.data.inventory || []);
      setKitchenName(res.data.kitchen_name || "");
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("No kitchen assigned to your account");
      } else {
        console.error("Error fetching inventory:", error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchReport = async () => {
    try {
      let url = `${API}/api/ops-extended/my-inventory-report?user_id=${user.user_id}&limit=100`;
      if (reportFilter !== "all") {
        url += `&raw_material_id=${reportFilter}`;
      }
      const res = await axios.get(url);
      setReport(res.data);
    } catch (error) {
      console.error("Error fetching report:", error);
      toast.error("Failed to load report");
    }
  };

  const openAdjustDialog = (item, type) => {
    setSelectedItem(item);
    setAdjustment({ value: type === "add" ? 1 : -1, reason: "" });
    setIsAdjustDialogOpen(true);
  };

  const handleAdjust = async () => {
    if (adjustment.value === 0) {
      toast.error("Adjustment value cannot be zero");
      return;
    }
    if (!adjustment.reason.trim()) {
      toast.error("Please provide a reason for this adjustment");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.put(
        `${API}/api/ops-extended/my-kitchen-inventory/${selectedItem.inventory_id}/adjust?user_id=${user.user_id}`,
        {
          adjustment: adjustment.value,
          reason: adjustment.reason
        }
      );
      toast.success(`Inventory ${adjustment.value > 0 ? 'added' : 'reduced'} successfully`);
      setIsAdjustDialogOpen(false);
      setSelectedItem(null);
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to adjust inventory");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openReportDialog = () => {
    fetchReport();
    setIsReportDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!kitchenName) {
    return (
      <div className="p-6">
        <Card className="border-0 shadow-card">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-yellow-500" />
            <h2 className="text-xl font-semibold mb-2">No Kitchen Assigned</h2>
            <p className="text-muted-foreground">
              You are not assigned to any kitchen. Please contact your administrator.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <Package className="w-6 h-6 text-primary" />
            Kitchen Inventory
          </h1>
          <p className="text-muted-foreground mt-1 flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            {kitchenName}
          </p>
        </div>
        <Button onClick={openReportDialog} variant="outline" className="rounded-full">
          <History className="w-4 h-4 mr-2" /> Transaction History
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-0 shadow-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Items</p>
                <p className="text-2xl font-bold">{inventory.length}</p>
              </div>
              <Package className="w-8 h-8 text-primary opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-card bg-yellow-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-600">Low Stock</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {inventory.filter(i => i.quantity <= 10 && i.quantity > 0).length}
                </p>
              </div>
              <AlertTriangle className="w-8 h-8 text-yellow-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-card bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600">Out of Stock</p>
                <p className="text-2xl font-bold text-red-600">
                  {inventory.filter(i => i.quantity <= 0).length}
                </p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Inventory Table */}
      <Card className="border-0 shadow-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Raw Materials Inventory</CardTitle>
        </CardHeader>
        <CardContent>
          {inventory.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No inventory items in your kitchen</p>
              <p className="text-sm mt-2">Contact admin to add raw materials to your kitchen</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Raw Material</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead>Current Quantity</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inventory.map((item) => (
                    <TableRow 
                      key={item.inventory_id}
                      className={item.quantity <= 0 ? 'bg-red-50' : item.quantity <= 10 ? 'bg-yellow-50' : ''}
                    >
                      <TableCell className="font-medium">{item.raw_material_name}</TableCell>
                      <TableCell>{item.unit}</TableCell>
                      <TableCell>
                        <span className={`text-lg font-bold ${
                          item.quantity <= 0 ? 'text-red-600' : 
                          item.quantity <= 10 ? 'text-yellow-600' : 
                          'text-green-600'
                        }`}>
                          {item.quantity}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={item.quantity <= 0 ? 'destructive' : item.quantity <= 10 ? 'warning' : 'default'}
                          className={
                            item.quantity <= 0 ? 'bg-red-500' : 
                            item.quantity <= 10 ? 'bg-yellow-500' : 
                            'bg-green-500'
                          }
                        >
                          {item.quantity <= 0 ? 'Out of Stock' : item.quantity <= 10 ? 'Low Stock' : 'In Stock'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-600 border-green-600 hover:bg-green-50"
                            onClick={() => openAdjustDialog(item, 'add')}
                            data-testid={`add-${item.inventory_id}`}
                          >
                            <Plus className="w-4 h-4 mr-1" /> Add
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-600 hover:bg-red-50"
                            onClick={() => openAdjustDialog(item, 'subtract')}
                            disabled={item.quantity <= 0}
                            data-testid={`subtract-${item.inventory_id}`}
                          >
                            <Minus className="w-4 h-4 mr-1" /> Use
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Adjustment Dialog */}
      <Dialog open={isAdjustDialogOpen} onOpenChange={setIsAdjustDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {adjustment.value > 0 ? 'Add to' : 'Reduce from'} Inventory
            </DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <div className="py-4 space-y-4">
              <div className="p-4 bg-muted/30 rounded-lg">
                <p className="font-medium">{selectedItem.raw_material_name}</p>
                <p className="text-sm text-muted-foreground">
                  Current: <span className="font-bold">{selectedItem.quantity} {selectedItem.unit}</span>
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Quantity to {adjustment.value > 0 ? 'Add' : 'Remove'} *</Label>
                <Input
                  type="number"
                  min="0.1"
                  step="0.1"
                  value={Math.abs(adjustment.value)}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) || 0;
                    setAdjustment({ 
                      ...adjustment, 
                      value: adjustment.value > 0 ? val : -val 
                    });
                  }}
                  data-testid="adjustment-value"
                />
              </div>

              <div className="space-y-2">
                <Label>Reason *</Label>
                <Input
                  value={adjustment.reason}
                  onChange={(e) => setAdjustment({ ...adjustment, reason: e.target.value })}
                  placeholder={adjustment.value > 0 ? "e.g., Stock received from supplier" : "e.g., Used for production"}
                  data-testid="adjustment-reason"
                />
              </div>

              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>New quantity:</strong> {selectedItem.quantity} {adjustment.value > 0 ? '+' : ''} {adjustment.value} = {' '}
                  <span className="font-bold">{selectedItem.quantity + adjustment.value} {selectedItem.unit}</span>
                </p>
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setIsAdjustDialogOpen(false)} className="rounded-full">
              Cancel
            </Button>
            <Button 
              onClick={handleAdjust} 
              disabled={isSubmitting}
              className={`rounded-full ${adjustment.value > 0 ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {adjustment.value > 0 ? 'Add Stock' : 'Remove Stock'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report Dialog */}
      <Dialog open={isReportDialogOpen} onOpenChange={setIsReportDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <History className="w-5 h-5" />
              Inventory Transaction History
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {/* Filter */}
            <div className="mb-4">
              <Label>Filter by Raw Material</Label>
              <Select value={reportFilter} onValueChange={(v) => { setReportFilter(v); }}>
                <SelectTrigger className="w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Materials</SelectItem>
                  {inventory.map((item) => (
                    <SelectItem key={item.raw_material_id} value={item.raw_material_id}>
                      {item.raw_material_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="link" size="sm" onClick={fetchReport}>Apply Filter</Button>
            </div>

            {/* Summary */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <Card className="bg-muted/30">
                <CardContent className="p-3 text-center">
                  <p className="text-xs text-muted-foreground">Transactions</p>
                  <p className="text-xl font-bold">{report.summary?.total_transactions || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-green-50">
                <CardContent className="p-3 text-center">
                  <p className="text-xs text-green-600">Additions</p>
                  <p className="text-xl font-bold text-green-600">+{report.summary?.total_additions || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-red-50">
                <CardContent className="p-3 text-center">
                  <p className="text-xs text-red-600">Subtractions</p>
                  <p className="text-xl font-bold text-red-600">-{report.summary?.total_subtractions || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-blue-50">
                <CardContent className="p-3 text-center">
                  <p className="text-xs text-blue-600">Net Change</p>
                  <p className={`text-xl font-bold ${(report.summary?.net_change || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(report.summary?.net_change || 0) >= 0 ? '+' : ''}{report.summary?.net_change || 0}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Table */}
            {report.transactions?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No transactions recorded yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date & Time</TableHead>
                      <TableHead>Raw Material</TableHead>
                      <TableHead>Change</TableHead>
                      <TableHead>Before → After</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>By</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.transactions?.map((txn) => (
                      <TableRow key={txn.transaction_id}>
                        <TableCell className="text-sm">
                          {new Date(txn.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell>{txn.raw_material_name}</TableCell>
                        <TableCell>
                          <Badge className={txn.adjustment >= 0 ? 'bg-green-500' : 'bg-red-500'}>
                            {txn.adjustment >= 0 ? '+' : ''}{txn.adjustment}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {txn.quantity_before} → {txn.quantity_after}
                        </TableCell>
                        <TableCell className="text-sm max-w-[200px] truncate">{txn.reason}</TableCell>
                        <TableCell className="text-sm">{txn.user_name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsReportDialogOpen(false)} className="rounded-full">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
